// File: src/components/BotFlowGraph.jsx
import React, { useCallback, useEffect, useState } from "react";
import ReactFlow, {
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  addEdge,
  MarkerType,
  Panel,
  applyEdgeChanges
} from "reactflow";
import "reactflow/dist/style.css";
import { getStructuredLayout } from "../../../utils/dagreUtils";




export default function BotFlowGraph({ allQuestions, onUpdateConnections,onEditQuestion,onDeleteConnection }) {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges] = useEdgesState([]);
  const [pendingConnection, setPendingConnection] = useState(null);
  const [branchOption, setBranchOption] = useState("");

useEffect(() => {
  const rawNodes = Object.values(allQuestions).map((q) => ({
    id: q.id,
    type: "default",
    data: { label: `${q.response_key || "Q"}: ${q.question}` },
    position: { x: 0, y: 0 } // will be set by dagre
  }));

  const newEdges = [];

  for (const q of Object.values(allQuestions)) {
    if (q.next) {
      newEdges.push({
        id: `${q.id}->${q.next}`,
        source: q.id,
        target: q.next,
        markerEnd: { type: MarkerType.ArrowClosed }
      });
    }

    if (q.branches) {
      for (const b of q.branches) {
        if (b.next) {
          newEdges.push({
            id: `${q.id}->${b.next}-${b.value}`,
            source: q.id,
            target: b.next,
            label: b.value,
            markerEnd: { type: MarkerType.ArrowClosed }
          });
        }
      }
    }

    if (q.api_loop?.exit_to) {
      newEdges.push({
        id: `${q.id}->${q.api_loop.exit_to}-api`,
        source: q.id,
        target: q.api_loop.exit_to,
        label: "API Exit",
        style: { stroke: "#555" },
        markerEnd: { type: MarkerType.ArrowClosed }
      });
    }
  }

  const layoutedNodes = getStructuredLayout(rawNodes, newEdges, "TB"); // TB = Top to Bottom

  setNodes(layoutedNodes);
  setEdges(newEdges);
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [allQuestions]);

const handleEdgesChange = useCallback(
  (changes) => {
    setEdges((eds) => {
      const updatedEdges = applyEdgeChanges(changes, eds);

      changes.forEach(change => {
        if (change.type === 'remove') {
          const removedEdge = eds.find(e => e.id === change.id);
          if (removedEdge && onDeleteConnection) {
            onDeleteConnection({
              source: removedEdge.source,
              target: removedEdge.target,
              label: removedEdge.label || null
            });
          }
        }
      });

      return updatedEdges;
    });
  },
  [setEdges, onDeleteConnection]
);


  const onConnect = useCallback(
    ({ source, target }) => {
      const sourceQ = allQuestions[source];
      if (!sourceQ) return;

      if (sourceQ.response_type === "multiple_choice" || sourceQ.response_type === "dropdown") {
        const usedBranches = sourceQ.branches?.map(b => b.value) || [];
        const available = sourceQ.options?.filter(opt => !usedBranches.includes(opt)) || [];

        if (available.length === 0) {
          alert("All options are already connected.");
          return;
        }

        setPendingConnection({ source, target });
        return;
      }

      const newEdge = {
        id: `${source}->${target}`,
        source,
        target,
        markerEnd: { type: MarkerType.ArrowClosed }
      };
      setEdges((eds) => addEdge(newEdge, eds));
      onUpdateConnections({ source, target });
    },
    [allQuestions, setEdges, onUpdateConnections]
  );

  const confirmBranchConnection = () => {
    const { source, target } = pendingConnection;
    const newEdge = {
      id: `${source}->${target}-${branchOption}`,
      source,
      target,
      label: branchOption,
      markerEnd: { type: MarkerType.ArrowClosed }
    };

    setEdges((eds) => addEdge(newEdge, eds));
    onUpdateConnections({ source, target, label: branchOption });
    setPendingConnection(null);
    setBranchOption("");
  };

  const cancelBranchConnection = () => {
    setPendingConnection(null);
    setBranchOption("");
  };

  const availableOptions = pendingConnection?.source
    ? allQuestions[pendingConnection.source]?.options?.filter(opt => {
        const used = allQuestions[pendingConnection.source]?.branches?.some(b => b.value === opt);
        return !used;
      })
    : [];

  return (
    <div style={{ height: 600 }} className="border rounded relative">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={handleEdgesChange}
        onConnect={onConnect}
        fitView
        onNodeClick={(event, node) => {
    if (onEditQuestion) {
      onEditQuestion(node.id); // Send the node ID to parent
    }
  }}
      >
        <Background />
        {/* <MiniMap /> */}
        <Controls />
      </ReactFlow>

      {pendingConnection && (
        <Panel position="top-right" className="bg-white p-4 border shadow rounded space-y-2 w-64">
          <h4 className="font-bold">Select Option for Branch</h4>
          <p className="text-sm text-gray-600">
            Linking <strong>{pendingConnection.source}</strong> ➝ <strong>{pendingConnection.target}</strong>
          </p>
          <select
            className="border p-2 w-full"
            value={branchOption}
            onChange={(e) => setBranchOption(e.target.value)}
          >
            <option value="">-- Select Option --</option>
            {availableOptions.map((opt, idx) => (
              <option key={idx} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          <div className="flex justify-between gap-2">
            <button
              className="bg-green-600 text-white px-3 py-1 rounded text-sm"
              onClick={confirmBranchConnection}
              disabled={!branchOption}
            >
              Confirm
            </button>
            <button
              className="bg-gray-300 px-3 py-1 rounded text-sm"
              onClick={cancelBranchConnection}
            >
              Cancel
            </button>
          </div>
        </Panel>
      )}
    </div>
  );
}
